import { useEffect, useRef } from 'react'

import { EDITOR_DRAG_DETECTION_THRESHOLD } from '@editor/config/constants'
import { CursorState } from '@editor/config/constants/cursor'
import { PolylineProps } from '@editor/interfaces/canvas'
import { BlueprintEditorTools } from '@editor/pages/projects/blueprint-editor/definitions'
import { calculateIndexInPolyline } from '@editor/services/Polyline'
import { useAppDispatch, useAppSelector } from '@editor/store/app'
import { useToolUtilSelector } from '@editor/store/selectors'
import {
  setCursor,
  setEditingElementId,
  setHoveredElementId,
  setSelectedElementIds,
  toggleSelectedElementIds,
} from '@editor/store/tool'
import { ThreeEvent } from '@react-three/fiber'
import { Vector2 } from 'three'

import useConfigs from './useConfigs'

const useEventHandlers = (props: PolylineProps, configs: ReturnType<typeof useConfigs>) => {
  const { id, points, vertexAnchors, isWorking, onEnter, onLeave, onDown } = props
  const {
    currentPositionsRef,
    isAddingVertex,
    isRemovingVertex,
    isDraggingRef,
    dragStartPosRef,
    isDraggingVertexRef,
    pointOnLine,
    setPointOnLine,
  } = configs

  // Store
  const dispatch = useAppDispatch()
  const selectedTool = useAppSelector((state) => state.blueprint.editor.selectedTool)
  const editingElementId = useToolUtilSelector((state) => state.editingElementId)
  const currentAnchorPosition = useToolUtilSelector((state) => state.currentAnchorPosition)

  // Refs
  const movingStartLocalPointRef = useRef<Vector2 | null>(null)
  const tempV2Ref = useRef(new Vector2())

  // Derived
  const isSelectorTool = selectedTool === BlueprintEditorTools.Selector

  /**
   * Handle mouse enter event on the distance label.
   * 1. Sets the cursor to move.
   * 2. Sets the hovered element state.
   *
   * @param ev - The mouse event.
   */
  const handleMouseEnter = (ev: ThreeEvent<PointerEvent>) => {
    // don't allow hovering if editing anything
    if (editingElementId) return

    ev.stopPropagation()

    dispatch(setCursor(CursorState.POINTER))
    dispatch(setHoveredElementId(id))
    onEnter?.(ev)
  }

  /**
   * Handle mouse leave event on the distance label.
   * 1. Sets the cursor back to default.
   * 2. Clears any hovered element state.
   *
   * @param ev - The mouse event.
   */
  const handleMouseLeave = (ev: ThreeEvent<PointerEvent>) => {
    ev.stopPropagation()

    dispatch(setCursor(CursorState.NONE))
    dispatch(setHoveredElementId(''))
    setPointOnLine(undefined)
    onLeave?.(ev)
  }

  /**
   * Handle mouse move event on the polyline.
   * Active when Add Vertex or Remove Vertex (for edge cutting) subtool is selected.
   *
   * @param ev - The mouse event.
   */
  const handleMouseMove = (ev: ThreeEvent<PointerEvent>) => {
    ev.stopPropagation()

    if ((isAddingVertex || isRemovingVertex) && !isDraggingVertexRef.current) {
      setPointOnLine(ev.pointOnLine?.toArray())
    }
  }

  /**
   * Handle mouse down event on the distance label.
   *
   * @param ev - The mouse event.
   */
  const handleMouseDown = (ev: ThreeEvent<PointerEvent>) => {
    ev.stopPropagation()
    currentPositionsRef.current = points
    movingStartLocalPointRef.current = new Vector2(ev.clientX, ev.clientY)
    dragStartPosRef.current = currentAnchorPosition || null
    onDown?.(ev)
  }

  /**
   * Handle click event on the distance label.
   * Selects the distance label if not dragging and if selector tool is active.
   * When Remove Vertex tool is active, clicking on the edge will trigger a split (handled in parent).
   *
   * @param ev - The mouse event.
   */
  const handleClick = (ev: ThreeEvent<PointerEvent>) => {
    if (isDraggingRef.current || !isSelectorTool || isWorking) return

    ev.stopPropagation()

    if (isAddingVertex && pointOnLine) {
      // Figure out where to add the vertex
      const newIndex = calculateIndexInPolyline(points, pointOnLine)

      // Add a vertex in within the points collection
      const newPoints = [...points]
      newPoints.splice(newIndex, 0, pointOnLine)

      // Notify the parent about the updated points
      currentPositionsRef.current = newPoints
      vertexAnchors?.onUpdated?.(newPoints)
    } else if (!isRemovingVertex) {
      dispatch(setSelectedElementIds([id]))
    }
  }

  /**
   * Handle mouse up and mouse move events on window.
   * This is to capture events outside the distance label and canvas
   * if user's mouse goes out of bounds while dragging.
   */
  useEffect(() => {
    const handleUp = (ev: MouseEvent) => {
      movingStartLocalPointRef.current = null

      if (!isDraggingRef.current) return
      ev.stopPropagation()

      if (currentPositionsRef.current) vertexAnchors?.onUpdated?.(currentPositionsRef.current)
      isDraggingRef.current = false
      dragStartPosRef.current = null
      currentPositionsRef.current = null
      dispatch(setEditingElementId(undefined))
      dispatch(toggleSelectedElementIds({ ids: [id], selected: true }))
    }

    const handleMove = (ev: MouseEvent) => {
      if (movingStartLocalPointRef.current) {
        const distance = movingStartLocalPointRef.current.distanceTo(tempV2Ref.current.set(ev.clientX, ev.clientY))
        if (distance > EDITOR_DRAG_DETECTION_THRESHOLD && !isDraggingRef.current) {
          isDraggingRef.current = true
          dispatch(setEditingElementId(id))
          dispatch(setHoveredElementId(''))
        }
      }
    }

    window.addEventListener('mouseup', handleUp)
    window.addEventListener('mousemove', handleMove)
    return () => {
      window.removeEventListener('mouseup', handleUp)
      window.removeEventListener('mousemove', handleMove)
    }
  }, [dispatch, isDraggingRef, movingStartLocalPointRef, dragStartPosRef, currentPositionsRef, vertexAnchors, id])

  return {
    movingStartLocalPointRef,

    pointOnLine,

    handleMouseEnter,
    handleMouseLeave,
    handleMouseMove,
    handleMouseDown,
    handleClick,
  }
}

export default useEventHandlers
